// src/pages/auth/Register.jsx
import * as React from 'react';
import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
  InputAdornment,
  LinearProgress,
  Chip,
  Divider,
  Alert,
  Snackbar,
  Link,
} from '@mui/material';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { BASEURL } from '../../Constants';
import { SendLogin } from '../../services/login/loginService';
import { buildMfaChallenge, saveMfaChallenge } from '../../services/login/mfaChallenge';
import { setToken } from '../../components/jwtToken';

async function postRequest(url, body) {
  return fetch(BASEURL + url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then((r) => r.json());
}

const brandColor = '#005CC5';

export default function Register() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, type: 'success', msg: '' });

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  // Try to show who we’re registering (email from token)
  const decodedEmail = React.useMemo(() => {
    if (!token) return '';
    try {
      const decoded = jwtDecode(token);
      return decoded?.username || decoded?.email || '';
    } catch {
      return '';
    }
  }, [token]);

  // Password policy (adjust as needed)
  const policy = { minLength: 8, requireUpper: true, requireNumber: true, requireSymbol: true };

  const requirements = useMemo(() => {
    const hasMin = password.length >= policy.minLength;
    const hasUpper = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSymbol = /[^A-Za-z0-9]/.test(password);
    return { hasMin, hasUpper, hasNumber, hasSymbol };
  }, [password]);

  const strength = useMemo(() => {
    let score = 0;
    if (requirements.hasMin) score += 1;
    if (requirements.hasUpper) score += 1;
    if (requirements.hasNumber) score += 1;
    if (requirements.hasSymbol) score += 1;
    return score; // 0..4
  }, [requirements]);

  const strengthLabel = ['Very weak', 'Weak', 'Okay', 'Strong', 'Very strong'][strength] || 'Very weak';
  const strengthPercent = (strength / 4) * 100;

  const passwordsMatch = confirmPassword.length > 0 && confirmPassword === password;
  const tokenMissing = !token;
  const canSubmit = !tokenMissing && passwordsMatch && Object.values(requirements).every(Boolean) && strength >= 3 && !loading;

  const RequirementItem = ({ ok, label }) => (
    <Stack direction="row" spacing={1} alignItems="center">
      {ok ? (
        <CheckCircleRoundedIcon fontSize="small" sx={{ color: 'success.main' }} />
      ) : (
        <CancelRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
      )}
      <Typography variant="body2" color={ok ? 'text.primary' : 'text.secondary'}>
        {label}
      </Typography>
    </Stack>
  );

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setLoading(true);
      const response = await postRequest('/user/register/account', {
        token,
        password,
        confirm_password: confirmPassword,
      });

      if (response?.success) {
        // Continue through either direct login or the email verification flow.
        try {
          const email = decodedEmail || (jwtDecode(token)?.username ?? '');
          const loginResp = await SendLogin(email, password);
          if (loginResp?.success && loginResp?.mfa_required === false && loginResp?.token) {
            setToken(loginResp.token);
            setSnack({ open: true, type: 'success', msg: 'Account created! Redirecting...' });
            const destination = loginResp.onboarding_completed === 0 ? '/onboarding' : '/pentests';
            setTimeout(() => navigate(destination), 800);
            return;
          }
          if (loginResp?.success && loginResp?.mfa_required === true && loginResp?.challenge_token) {
            const challenge = buildMfaChallenge(loginResp);
            saveMfaChallenge(challenge);
            setSnack({ open: true, type: 'success', msg: 'Account created! Check your email to continue.' });
            setTimeout(() => navigate('/'), 800);
            return;
          }
        } catch {
          /* fallthrough to generic error */
        }
        setSnack({ open: true, type: 'error', msg: 'Registered, but login failed. Please sign in.' });
      } else {
        setSnack({ open: true, type: 'error', msg: response?.message || 'Registration failed.' });
      }
    } catch {
      setSnack({ open: true, type: 'error', msg: 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        p: 2,
        background: `radial-gradient(1200px 600px at 20% -10%, rgba(0,92,197,0.15), transparent 60%),
                     radial-gradient(800px 400px at 120% 20%, rgba(0,92,197,0.12), transparent 60%),
                     linear-gradient(135deg, #f8fbff 0%, #eef3ff 100%)`,
      }}
    >
      <Card elevation={6} sx={{ width: '100%', maxWidth: 720, borderRadius: 4, overflow: 'hidden' }}>
        {/* Header */}
        <Box
          sx={{
            px: { xs: 3, sm: 4 },
            py: 3,
            background: `linear-gradient(135deg, ${brandColor} 0%, #003a88 100%)`,
            color: 'white',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                backgroundColor: 'rgba(255,255,255,0.18)',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <PersonAddAltRoundedIcon />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
                Finish setting up your account
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {decodedEmail ? `Creating password for ${decodedEmail}` : 'Create a strong password to get started.'}
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to="/"
              size="small"
              startIcon={<ArrowBackRoundedIcon />}
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.6)' }}
              variant="outlined"
            >
              Sign in
            </Button>
          </Stack>
        </Box>

        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          {tokenMissing && (
            <Alert severity="error" sx={{ mb: 3 }}>
              This invite/registration link is invalid or has expired.{' '}
              <Link component={RouterLink} to="/" underline="hover">
                Back to sign in
              </Link>
              .
            </Alert>
          )}

          <form onSubmit={handleRegister} noValidate>
            <Stack spacing={2.5}>
              <TextField
                label="New password"
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPw((v) => !v)} edge="end" aria-label="toggle password visibility">
                        {showPw ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Strength meter */}
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                  <Typography variant="caption" color="text.secondary">
                    Strength:
                  </Typography>
                  <Chip
                    size="small"
                    label={strengthLabel}
                    sx={{
                      bgcolor:
                        strength >= 4
                          ? 'success.light'
                          : strength === 3
                          ? 'warning.light'
                          : 'error.light',
                    }}
                  />
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={strengthPercent}
                  sx={{ height: 8, borderRadius: 999, '& .MuiLinearProgress-bar': { borderRadius: 999 } }}
                />
              </Box>

              <TextField
                label="Confirm new password"
                type={showPw2 ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
                fullWidth
                error={confirmPassword.length > 0 && !passwordsMatch}
                helperText={confirmPassword.length > 0 && !passwordsMatch ? 'Passwords do not match.' : ' '}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPw2((v) => !v)} edge="end" aria-label="toggle password visibility">
                        {showPw2 ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Divider />

              {/* Requirements */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Password requirements
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap">
                  <RequirementItem ok={requirements.hasMin} label={`At least ${policy.minLength} characters`} />
                  <RequirementItem ok={requirements.hasUpper} label="One uppercase letter" />
                  <RequirementItem ok={requirements.hasNumber} label="One number" />
                  <RequirementItem ok={requirements.hasSymbol} label="One symbol (!@#$%^&*)" />
                </Stack>
              </Box>

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={!canSubmit}
                fullWidth
                sx={{
                  py: 1.2,
                  textTransform: 'none',
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${brandColor} 0%, #003a88 100%)`,
                  boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
                }}
              >
                {loading ? 'Creating account…' : 'Create password & continue'}
              </Button>

              <Stack direction="row" justifyContent="center">
                <Typography variant="body2" color="text.secondary">
                  Already have an account?{' '}
                  <Link component={RouterLink} to="/" underline="hover">
                    Back to sign in
                  </Link>
                </Typography>
              </Stack>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.type === 'success' ? 'success' : 'error'}
          variant="filled"
          elevation={6}
          sx={{ width: '100%' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
